'use client';
import React, { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';


export type SimulateChannel = 'voice' | 'livechat' | 'whatsapp' | 'email' | 'x' | 'facebook' | 'instagram';

interface Props {
  onSimulate: (channel: SimulateChannel) => void;
  onReset: () => void;
  /** True only when a GENUINELY active simulated voice call is connected (not just seeded demo data) */
  activeVoiceIsSimulated?: boolean;
  /** The citizen name of the active simulated voice call, if any */
  activeVoiceCitizen?: string;
  /** Called when presenter chooses "End Current Demo & Start New Call" */
  onEndAndRestartVoice?: () => void;
}

export default function SimulateDemoDropdown({ onSimulate, onReset, activeVoiceIsSimulated, activeVoiceCitizen, onEndAndRestartVoice }: Props) {
  const [open, setOpen] = useState(false);
  const [socialOpen, setSocialOpen] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showActiveVoiceDialog, setShowActiveVoiceDialog] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSocialOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChannel = (channel: SimulateChannel) => {
    setOpen(false);
    setSocialOpen(false);

    if (channel === 'voice' && activeVoiceIsSimulated) {
      // Genuinely active simulated call — offer options
      setShowActiveVoiceDialog(true);
      return;
    }
    // For voice with only seeded demo data (not genuinely active), or any other channel:
    // proceed directly — the parent will reset seeded state transparently
    onSimulate(channel);
  };

  const handleReset = () => {
    setShowResetConfirm(false);
    setOpen(false);
    onReset();
  };

  const handleEndAndRestart = () => {
    setShowActiveVoiceDialog(false);
    onEndAndRestartVoice?.();
  };

  return (
    <>
      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen(v => !v)}
          className="btn-secondary px-3 py-1.5 flex items-center gap-1.5 text-[12px] font-600"
        >
          <Icon name="PlayCircleIcon" size={13} className="text-primary" />
          Simulate Demo
          <Icon name="ChevronDownIcon" size={11} />
        </button>

        {open && (
          <div className="absolute top-full right-0 mt-1.5 w-64 bg-card border border-border rounded-xl shadow-elevated z-[200] py-1.5 slide-in-up">
            <div className="px-3 py-1.5 border-b border-border mb-1">
              <div className="text-[10px] font-700 uppercase tracking-widest text-muted-foreground">Simulate Demo</div>
              <div className="text-[9px] text-muted-foreground mt-0.5">Demo utility — not a production function</div>
            </div>

            {/* Voice */}
            <div className="px-2 py-1">
              <button
                onClick={() => handleChannel('voice')}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] hover:bg-muted rounded-lg transition-colors text-left"
              >
                <div className="w-6 h-6 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Icon name="PhoneIcon" size={12} className="text-blue-600" />
                </div>
                <div>
                  <div className="font-600 text-foreground">Voice Call</div>
                  <div className="text-[10px] text-muted-foreground">Incoming citizen phone call</div>
                </div>
              </button>
            </div>

            <div className="border-t border-border mx-2 my-1" />

            {/* Digital channels */}
            <div className="px-2 py-1">
              <button
                onClick={() => handleChannel('livechat')}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] hover:bg-muted rounded-lg transition-colors text-left"
              >
                <div className="w-6 h-6 rounded-lg bg-cyan-50 flex items-center justify-center flex-shrink-0">
                  <Icon name="ChatBubbleLeftRightIcon" size={12} className="text-cyan-600" />
                </div>
                <div>
                  <div className="font-600 text-foreground">Live Chat</div>
                  <div className="text-[10px] text-muted-foreground">Incoming real-time website chat</div>
                </div>
              </button>

              <button
                onClick={() => handleChannel('whatsapp')}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] hover:bg-muted rounded-lg transition-colors text-left"
              >
                <div className="w-6 h-6 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                  <Icon name="ChatBubbleOvalLeftEllipsisIcon" size={12} className="text-green-600" />
                </div>
                <div>
                  <div className="font-600 text-foreground">WhatsApp</div>
                  <div className="text-[10px] text-muted-foreground">Incoming WhatsApp conversation</div>
                </div>
              </button>

              <button
                onClick={() => handleChannel('email')}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] hover:bg-muted rounded-lg transition-colors text-left"
              >
                <div className="w-6 h-6 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                  <Icon name="EnvelopeIcon" size={12} className="text-purple-600" />
                </div>
                <div>
                  <div className="font-600 text-foreground">Email</div>
                  <div className="text-[10px] text-muted-foreground">Incoming citizen email</div>
                </div>
              </button>

              {/* Social Media with submenu */}
              <div className="relative">
                <button
                  onClick={() => setSocialOpen(v => !v)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] hover:bg-muted rounded-lg transition-colors text-left"
                >
                  <div className="w-6 h-6 rounded-lg bg-pink-50 flex items-center justify-center flex-shrink-0">
                    <Icon name="HashtagIcon" size={12} className="text-pink-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-600 text-foreground">Social Media</div>
                    <div className="text-[10px] text-muted-foreground">X, Facebook, Instagram</div>
                  </div>
                  <Icon name="ChevronRightIcon" size={11} className="text-muted-foreground" />
                </button>

                {socialOpen && (
                  <div className="absolute left-full top-0 ml-1 w-48 bg-card border border-border rounded-xl shadow-elevated z-[210] py-1.5 slide-in-up">
                    <div className="px-3 py-1 text-[10px] font-700 uppercase tracking-widest text-muted-foreground border-b border-border mb-1">
                      Social Media
                    </div>
                    <button onClick={() => handleChannel('x')}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] hover:bg-muted rounded-lg transition-colors text-left">
                      <span className="w-5 h-5 rounded bg-slate-100 flex items-center justify-center text-[11px] font-700 text-slate-800 flex-shrink-0">𝕏</span>
                      <span className="font-500 text-foreground">X</span>
                    </button>
                    <button onClick={() => handleChannel('facebook')}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] hover:bg-muted rounded-lg transition-colors text-left">
                      <div className="w-5 h-5 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Icon name="UserGroupIcon" size={11} className="text-blue-700" />
                      </div>
                      <span className="font-500 text-foreground">Facebook</span>
                    </button>
                    <button onClick={() => handleChannel('instagram')}
                      className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] hover:bg-muted rounded-lg transition-colors text-left">
                      <div className="w-5 h-5 rounded bg-pink-100 flex items-center justify-center flex-shrink-0">
                        <Icon name="CameraIcon" size={11} className="text-pink-600" />
                      </div>
                      <span className="font-500 text-foreground">Instagram</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Divider + Reset */}
            <div className="border-t border-border mx-2 my-1" />
            <div className="px-2 py-1">
              <button
                onClick={() => { setOpen(false); setShowResetConfirm(true); }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] hover:bg-red-50 rounded-lg transition-colors text-left text-red-600"
              >
                <Icon name="ArrowPathIcon" size={13} className="text-red-500" />
                <span className="font-600">Reset Demo State</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Active Voice Dialog — only shown when a GENUINELY active simulated call exists */}
      {showActiveVoiceDialog && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40">
          <div className="bg-card border border-border rounded-2xl shadow-modal p-6 w-[420px] slide-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Icon name="PhoneIcon" size={18} className="text-blue-600" />
              </div>
              <div>
                <div className="text-[15px] font-700 text-foreground">Voice Call Currently Active</div>
                <div className="text-[12px] text-muted-foreground">{activeVoiceCitizen || 'Ahmed Firdaus'} is currently connected.</div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-5 text-[12px] text-blue-700">
              A simulated Voice Call is currently active. You can return to the current call, end it and start a new simulation, or cancel.
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setShowActiveVoiceDialog(false)}
                className="btn-primary py-2.5 text-[13px] flex items-center justify-center gap-2"
              >
                <Icon name="PhoneIcon" size={14} />
                Return to Current Call
              </button>
              <button
                onClick={handleEndAndRestart}
                className="btn-secondary py-2.5 text-[13px] flex items-center justify-center gap-2"
              >
                <Icon name="ArrowPathIcon" size={14} />
                End Current Demo &amp; Start New Call
              </button>
              <button
                onClick={() => setShowActiveVoiceDialog(false)}
                className="py-2 text-[12px] text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40">
          <div className="bg-card border border-border rounded-2xl shadow-modal p-6 w-96 slide-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                <Icon name="ArrowPathIcon" size={18} className="text-red-500" />
              </div>
              <div>
                <div className="text-[15px] font-700 text-foreground">Reset Demo State</div>
                <div className="text-[12px] text-muted-foreground">This will restore the initial demo configuration</div>
              </div>
            </div>
            <p className="text-[13px] text-foreground mb-5 leading-relaxed">
              Reset all demo interactions and return to initial demo state?
            </p>
            <div className="bg-muted/50 rounded-xl p-3 mb-5 text-[11px] text-muted-foreground space-y-1">
              <div>• Clear all simulated incoming interactions</div>
              <div>• Return preset Active Interactions</div>
              <div>• Restore preset Cases and Tickets</div>
              <div>• Reset call state, MIR status, AI status</div>
              <div>• Return Nur Aisyah to Available</div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowResetConfirm(false)} className="flex-1 btn-secondary py-2.5 text-[13px]">
                Cancel
              </button>
              <button onClick={handleReset} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-xl font-700 text-[13px] transition-colors">
                Reset Demo
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
